import React, { useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  getInterruptData,
  getInterruptOpen,
} from '@atredux/selectors/interruptSelector'

import Modal from '@amzn/meridian/modal'
import Row from '@amzn/meridian/row'
import Text from '@amzn/meridian/text'
import Button from '@amzn/meridian/button'
import Divider from '@amzn/meridian/divider'
import Column from '@amzn/meridian/column'

import { clearInterrupt, IInterruptData } from '@atredux/slices/interruptSlice'
import { CrossFrameUtils } from '@amzn/jwo-store-management-platform-components'
import { useBundle } from '@amzn/react-arb-tools'
import { BUNDLE_NAME } from '@src/constants'
import { t } from '@src/translationHelper'

interface IInterruptOptionButtonsProps {
    closeInterrupt: () => void
    interruptData: IInterruptData
}

const yesCallback = (target) => {
    CrossFrameUtils.sendMessageToOtherFrame({
        frame: target.source,
        message: {
            method: 'interruptYesCallback',
        },
    })
}

const noCallback = (target) => {
    CrossFrameUtils.sendMessageToOtherFrame({
        frame: target.source,
        message: {
            method: 'interruptNoCallback',
        },
    })
}

const snoozeCallback = (target) => {
    CrossFrameUtils.sendMessageToOtherFrame({
        frame: target.source,
        message: {
            method: 'interruptSnoozeCallback',
        },
    })
}

const InterruptOptionButtons = (
  props: IInterruptOptionButtonsProps,
): React.ReactElement => {
    const [bundle] = useBundle(BUNDLE_NAME)
    const { closeInterrupt, interruptData } = props
    const boundCallback = (cb: (target) => void) => {
        return () => {
            cb(interruptData.target)
            closeInterrupt()
        }
    }

    return (
    <>
      {interruptData.yesCallback && (
        <Row alignmentHorizontal={'center'} alignmentVertical={'center'}>
          <Button
            type={'primary'}
            minWidth={'100%'}
            onClick={boundCallback(yesCallback)}
          >
            {t(bundle, 'ihm-associatetoolsbase-interrupt-yes-button')}
          </Button>
        </Row>
      )}
      {interruptData.noCallback && (
        <Row alignmentHorizontal={'center'} alignmentVertical={'center'}>
          <Button
            type={'secondary'}
            minWidth={'100%'}
            onClick={boundCallback(noCallback)}
          >
            {t(bundle, 'ihm-associatetoolsbase-interrupt-no-button')}
          </Button>
        </Row>
      )}
      {interruptData.snoozeCallback && (
        <Row alignmentHorizontal={'center'} alignmentVertical={'center'}>
          <Button
            type={'tertiary'}
            minWidth={'100%'}
            onClick={boundCallback(snoozeCallback)}
          >
            {t(bundle, 'ihm-associatetoolsbase-interrupt-snooze-button')}
          </Button>
        </Row>
      )}
    </>
    )
}

const InterruptModal = (): React.ReactElement => {
    const [bundle] = useBundle(BUNDLE_NAME)
    const dispatch = useDispatch()
    const open = useSelector(getInterruptOpen)
    const interruptData: IInterruptData = useSelector(getInterruptData)
    const hasAnyCallbacks =
    interruptData.noCallback ||
    interruptData.yesCallback ||
    interruptData.snoozeCallback

    const closeInterrupt = useCallback(() => {
        dispatch(clearInterrupt())
    }, [dispatch])

    return (
    <Modal
      open={open}
      onClose={!hasAnyCallbacks && closeInterrupt}
      scrollContainer="viewport"
      closeLabel={t(bundle, 'jsma-web-common-close')}
      width="90vw"
    >
      <Column alignmentVertical={'center'}>
        <Row alignmentHorizontal={'center'} alignmentVertical={'center'}>
          <Text>{interruptData.message}</Text>
        </Row>
        <Divider size={'large'} />
        {InterruptOptionButtons({ closeInterrupt, interruptData })}
      </Column>
    </Modal>
    )
}

export default InterruptModal
